import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition - Wraps page content with smooth enter/exit animations
 * Uses CSS transitions triggered by route changes
 */
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionStage, setTransitionStage] = useState('entered');
  const previousPathRef = useRef(location.pathname);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip transition on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDisplayChildren(children);
      return;
    }

    // Check if the path actually changed
    if (location.pathname !== previousPathRef.current) {
      // Start exit animation
      setTransitionStage('exit');
      
      // After exit animation completes, update content and start enter animation
      const exitTimer = setTimeout(() => {
        setDisplayChildren(children);
        previousPathRef.current = location.pathname;
        setTransitionStage('enter');
        
        // After enter animation starts, set to entered state
        const enterTimer = setTimeout(() => {
          setTransitionStage('entered');
        }, 50);
        
        return () => clearTimeout(enterTimer);
      }, 150); // Exit animation duration

      return () => clearTimeout(exitTimer);
    } else {
      // Same path, just update children (e.g., search params change)
      setDisplayChildren(children);
    }
  }, [location.pathname, children]);

  return (
    <div 
      className={`page-transition page-transition-${transitionStage}`}
    >
      {displayChildren}
    </div>
  );
};

export default PageTransition;
